<template>
    <div class="trending">
        <div class="section">
            <div class="section-title">Trending Tags</div>
            <div class="pill-row">
                <v-skeleton-loader
                    v-if="loadingTag"
                    type="chip, chip, chip, chip, chip"
                    class="mb-2"
                />
                <template v-else>
                    <v-chip
                        v-for="t in tagTerms"
                        :key="'tag-' + t.term"
                        class="pill"
                        variant="elevated"
                        color="primary"
                        @click="select('tag', t.term)"
                    >
                        #{{ t.term }} <span class="count">{{ formatCompact(t.hot_score) }}</span>
                    </v-chip>
                    <div v-if="!tagTerms.length" class="empty">No trending tags yet</div>
                </template>
            </div>
        </div>

        <div class="section">
            <div class="section-title">Trending Keywords</div>
            <div class="pill-row">
                <v-skeleton-loader
                    v-if="loadingKey"
                    type="chip, chip, chip, chip, chip"
                    class="mb-2"
                />
                <template v-else>
                    <v-chip
                        v-for="t in keyTerms"
                        :key="'key-' + t.term"
                        class="pill"
                        variant="tonal"
                        color="secondary"
                        @click="select('key', t.term)"
                    >
                        {{ t.term }} <span class="count">{{ formatCompact(t.hot_score) }}</span>
                    </v-chip>
                    <div v-if="!keyTerms.length" class="empty">No trending keywords yet</div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import SearchService, { TrendingTerm } from '@/services/SearchService';

const props = defineProps<{ limit?: number }>();
const emit = defineEmits<{ (e: 'select', p: { type: 'tag' | 'key'; term: string }): void }>();

const tagTerms = ref<TrendingTerm[]>([]);
const keyTerms = ref<TrendingTerm[]>([]);
const loadingTag = ref(true);
const loadingKey = ref(true);

onMounted(async () => {
    // Fetch both in parallel
    try {
        const [tagRes, keyRes] = await Promise.all([
            SearchService.trending('tag', props.limit ?? 20),
            SearchService.trending('key', props.limit ?? 20),
        ]);
        tagTerms.value = tagRes.terms ?? [];
        keyTerms.value = keyRes.terms ?? [];
    } finally {
        loadingTag.value = false;
        loadingKey.value = false;
    }
});

function select(type: 'tag' | 'key', term: string) {
    emit('select', { type, term });
}

function formatCompact(n?: number) {
    const v = typeof n === 'number' ? n : 0;
    try {
        return Intl.NumberFormat(undefined, { notation: 'compact' }).format(v);
    } catch {
        // Basic fallback
        if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
        if (v >= 1_000) return (v / 1_000).toFixed(1) + 'k';
        return String(v);
    }
}
</script>

<style scoped lang="scss">
.trending {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px 0 16px;
}
.section-title {
    font-weight: 600;
    color: #ddd;
    margin: 4px 0 8px;
}
.pill-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.pill {
    color: white;
}
.count {
    margin-left: 6px;
    opacity: 0.75;
}
.empty {
    color: #999;
    font-size: 14px;
    padding: 6px 0;
}
</style>
